import React, { useEffect, useRef, useState } from "react";
import TextField, { TextFieldProps } from "@mui/material/TextField";

/**
 * A controlled numeric <TextField> that can be fully cleared while typing.
 *
 * The long-standing bug across the admin UI: raw `type="number"` fields bound to
 * a number with an `onChange` that coerces `"" → 0` (or blocks empty) leave a
 * "stuck 0" you can't backspace away. This wrapper keeps the raw text internally
 * so the field can be empty mid-edit, reports the parsed number (or `null` when
 * empty) via `onValueChange`, and clamps to min/max — defaulting an empty field
 * to `clampEmptyTo` (or `min`) — on blur.
 *
 * Drop-in for `<TextField type="number" value={n} onChange={...}/>`:
 *   <NumberField value={qty} onValueChange={(v) => setQty(v ?? 1)} min={1} clampEmptyTo={1} />
 */
export type NumberFieldProps = Omit<TextFieldProps, "value" | "onChange" | "type"> & {
  value: number | null | undefined;
  onValueChange: (value: number | null) => void;
  min?: number;
  max?: number;
  /** Value to fall back to when the field is left empty/invalid on blur. */
  clampEmptyTo?: number | null;
};

const toText = (v: number | null | undefined) =>
  v === null || v === undefined || Number.isNaN(v) ? "" : String(v);

export default function NumberField({
  value,
  onValueChange,
  min,
  max,
  clampEmptyTo,
  onBlur,
  inputProps,
  ...rest
}: NumberFieldProps) {
  const [text, setText] = useState<string>(toText(value));
  const focused = useRef(false);

  // Keep the field in sync with external changes, but never clobber what the
  // user is actively typing (e.g. a transient empty string).
  useEffect(() => {
    if (!focused.current && toText(value) !== text) {
      setText(toText(value));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const clamp = (n: number) => {
    let out = n;
    if (typeof min === "number") out = Math.max(min, out);
    if (typeof max === "number") out = Math.min(max, out);
    return out;
  };

  return (
    <TextField
      {...rest}
      type="number"
      value={text}
      inputProps={{ ...(min != null ? { min } : {}), ...(max != null ? { max } : {}), ...inputProps }}
      onFocus={(e) => {
        focused.current = true;
        rest.onFocus?.(e);
      }}
      onChange={(e) => {
        const raw = e.target.value;
        setText(raw);
        if (raw === "" || raw === "-") {
          onValueChange(null);
          return;
        }
        const n = Number(raw);
        if (!Number.isNaN(n)) onValueChange(n);
      }}
      onBlur={(e) => {
        focused.current = false;
        const raw = e.target.value;
        let next: number | null;
        if (raw === "" || Number.isNaN(Number(raw))) {
          next = clampEmptyTo !== undefined ? clampEmptyTo : (min ?? null);
        } else {
          next = clamp(Number(raw));
        }
        setText(toText(next));
        onValueChange(next);
        onBlur?.(e);
      }}
    />
  );
}
